import React, { useCallback, useRef } from 'react';
import { useTheme } from '@mui/material';
import { Pin } from '../../utils/vibecheck';

interface Props {
  pin: Pin | null;
  onPlace?: (p: Pin) => void;       // omit when locked
  target?: { valence: number; energy: number } | null;
}

const SIZE = 100;
const clamp = (n: number) => Math.min(1, Math.max(0, n));
// valence runs left→right, energy bottom→top
const px = (v: number) => v * SIZE;
const py = (e: number) => (1 - e) * SIZE;

const BlitzGrid: React.FC<Props> = ({ pin, onPlace, target }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const svgRef = useRef<SVGSVGElement>(null);
  const dragging = useRef(false);
  const gridLine = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
  const label = theme.palette.text.secondary;

  const place = useCallback((e: React.PointerEvent) => {
    const r = svgRef.current?.getBoundingClientRect();
    if (!r || !onPlace) return;
    onPlace({
      x: Math.round(clamp((e.clientX - r.left) / r.width) * 1000) / 1000,
      y: Math.round(clamp(1 - (e.clientY - r.top) / r.height) * 1000) / 1000,
    });
  }, [onPlace]);

  const onKey = (e: React.KeyboardEvent) => {
    if (!onPlace) return;
    const step = e.shiftKey ? 0.1 : 0.04;
    const cur = pin ?? { x: 0.5, y: 0.5 };
    const moves: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] };
    const m = moves[e.key];
    if (!m) return;
    e.preventDefault();
    onPlace({ x: Math.round(clamp(cur.x + m[0]) * 1000) / 1000, y: Math.round(clamp(cur.y + m[1]) * 1000) / 1000 });
  };

  const desc = pin
    ? `Mood grid. Your pin is ${Math.round(pin.x * 100)} percent happy and ${Math.round(pin.y * 100)} percent intense.`
    : 'Mood grid. Horizontal axis runs from sad to happy, vertical from calm to intense. Tap or use arrow keys to place a pin.';

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gridTemplateRows: '1fr 22px', gap: 4, width: '100%', maxWidth: 420, margin: '0 auto' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="application"
        aria-label={desc}
        tabIndex={onPlace ? 0 : -1}
        onKeyDown={onKey}
        onPointerDown={(e) => { if (!onPlace) return; dragging.current = true; (e.currentTarget as Element).setPointerCapture?.(e.pointerId); place(e); }}
        onPointerMove={(e) => { if (dragging.current) place(e); }}
        onPointerUp={() => { dragging.current = false; }}
        onPointerCancel={() => { dragging.current = false; }}
        style={{
          width: '100%', height: 'auto', aspectRatio: '1 / 1', borderRadius: 14, display: 'block',
          border: `1px solid ${gridLine}`, cursor: onPlace ? 'crosshair' : 'default', touchAction: 'none', userSelect: 'none',
        }}
      >
        <defs>
          <linearGradient id="bz-h" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0" stopColor="#6366F1" stopOpacity={isDark ? 0.3 : 0.22} />
            <stop offset="1" stopColor="#F59E0B" stopOpacity={isDark ? 0.3 : 0.26} />
          </linearGradient>
          <linearGradient id="bz-v" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="#EF4444" stopOpacity={isDark ? 0.22 : 0.15} />
            <stop offset="1" stopColor="#22D3EE" stopOpacity={isDark ? 0.2 : 0.13} />
          </linearGradient>
          <clipPath id="bz-clip"><rect width={SIZE} height={SIZE} rx="3" /></clipPath>
        </defs>
        <g clipPath="url(#bz-clip)">
          <rect width={SIZE} height={SIZE} fill={isDark ? '#14142B' : '#FFFFFF'} />
          <rect width={SIZE} height={SIZE} fill="url(#bz-h)" />
          <rect width={SIZE} height={SIZE} fill="url(#bz-v)" />
          {[25, 50, 75].map(n => (
            <g key={n}>
              <line x1={n} y1={0} x2={n} y2={SIZE} stroke={gridLine} strokeWidth={n === 50 ? 0.6 : 0.3} strokeDasharray={n === 50 ? '2 2' : undefined} />
              <line x1={0} y1={n} x2={SIZE} y2={n} stroke={gridLine} strokeWidth={n === 50 ? 0.6 : 0.3} strokeDasharray={n === 50 ? '2 2' : undefined} />
            </g>
          ))}

          {pin && target && (
            <line x1={px(pin.x)} y1={py(pin.y)} x2={px(target.valence)} y2={py(target.energy)} stroke={theme.palette.text.secondary} strokeWidth={0.7} strokeDasharray="2 2" />
          )}
          {target && (
            <g>
              <circle cx={px(target.valence)} cy={py(target.energy)} r={7} fill="rgba(34,197,94,0.22)" />
              <circle cx={px(target.valence)} cy={py(target.energy)} r={3.4} fill="#22C55E" stroke="#fff" strokeWidth={0.8} />
            </g>
          )}
          {pin && (
            <g>
              <circle cx={px(pin.x)} cy={py(pin.y)} r={4.4} fill={theme.palette.primary.main} stroke="#fff" strokeWidth={1} />
              <circle cx={px(pin.x)} cy={py(pin.y)} r={1.3} fill="#fff" />
            </g>
          )}
          {/* Axis labels sit in the corners, inside the grid */}
          <g fontSize={3.6} fontWeight={700} fill={label} opacity={0.85} letterSpacing="0.2" style={{ pointerEvents: 'none' }}>
            <text transform="rotate(-90 3.6 3)" x={3.6} y={3} textAnchor="end">INTENSE</text>
            <text transform="rotate(-90 3.6 97)" x={3.6} y={97} textAnchor="start">CALM</text>
          </g>
          {!pin && onPlace && (
            <text x={50} y={51.5} textAnchor="middle" fontSize={5.2} fontWeight={600} fill={label} opacity={0.75} style={{ pointerEvents: 'none' }}>
              Tap to place your pin
            </text>
          )}
        </g>
      </svg>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: 700, color: label, letterSpacing: '0.04em' }} aria-hidden>
        <span>SAD</span>
        <span>HAPPY</span>
      </div>
    </div>
  );
};

export default BlitzGrid;
